/**
 * Thin fetch wrapper. All requests are same-origin (Vite proxies /api + /auth
 * to the worker in dev; Pages + Worker share an origin in prod), so cookies
 * ride along automatically with credentials: 'include'.
 */

import type {
  OtpInitiateResponse,
  OtpVerifyResponse,
  AuthUser,
} from '@sylvan/shared';

export class ApiError extends Error {
  status: number;
  code: string;
  constructor(status: number, code: string) {
    super(code);
    this.status = status;
    this.code = code;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    let code = `http_${res.status}`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body?.error) code = body.error;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(res.status, code);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  otpInitiate(email: string): Promise<OtpInitiateResponse> {
    return request('/auth/otp/initiate', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },
  otpVerify(authRequestId: string, code: string): Promise<OtpVerifyResponse> {
    return request('/auth/otp/verify', {
      method: 'POST',
      body: JSON.stringify({ auth_request_id: authRequestId, code }),
    });
  },
  logout(): Promise<void> {
    return request('/auth/logout', { method: 'POST' });
  },
  me(): Promise<{ user: AuthUser }> {
    return request('/api/me');
  },
};
